import { useEffect, useState, useRef } from "react";
import api, { fileUrl, formatApiError } from "@/lib/api";
import { usePageMeta } from "@/hooks/usePageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Tags, Loader2, Upload, Image as ImageIcon, Layers } from "lucide-react";

const empty = { name: "", description: "", image_id: null, sort_order: 0, is_active: true };

export default function AdminCategories() {
  usePageMeta("Manage Categories");
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();

  const [subOpen, setSubOpen] = useState(false);
  const [subCat, setSubCat] = useState(null);
  const [subs, setSubs] = useState([]);
  const [subName, setSubName] = useState("");
  const [subBusy, setSubBusy] = useState(false);

  const load = () => {
    setLoading(true);
    api.get("/admin/categories").then(({ data }) => setCats(data)).catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const openNew = () => { setEditing(null); setForm({ ...empty, sort_order: cats.length }); setOpen(true); };
  const openEdit = (c) => {
    setEditing(c);
    setForm({ name: c.name, description: c.description || "", image_id: c.image_id || null, sort_order: c.sort_order || 0, is_active: c.is_active });
    setOpen(true);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_id: data.image_id }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setSaving(true);
    const payload = { ...form, sort_order: parseInt(form.sort_order || 0, 10) };
    try {
      if (editing) await api.put(`/admin/categories/${editing.id}`, payload);
      else await api.post("/admin/categories", payload);
      toast.success(editing ? "Category updated" : "Category added");
      setOpen(false); load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete category "${c.name}"?`)) return;
    try { await api.delete(`/admin/categories/${c.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const openSubs = async (c) => {
    setSubCat(c); setSubs([]); setSubName(""); setSubOpen(true);
    try { const { data } = await api.get("/admin/subcategories", { params: { category_id: c.id } }); setSubs(data); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const reloadSubs = async (catId) => {
    const { data } = await api.get("/admin/subcategories", { params: { category_id: catId } });
    setSubs(data);
  };
  const addSub = async () => {
    if (!subName.trim()) return;
    setSubBusy(true);
    try {
      await api.post("/admin/subcategories", { category_id: subCat.id, name: subName.trim(), sort_order: subs.length });
      setSubName(""); await reloadSubs(subCat.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSubBusy(false); }
  };
  const toggleSub = async (s) => {
    try {
      await api.put(`/admin/subcategories/${s.id}`, { category_id: subCat.id, name: s.name, image_id: s.image_id || null, is_active: !s.is_active, sort_order: s.sort_order || 0 });
      await reloadSubs(subCat.id);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const removeSub = async (s) => {
    if (!window.confirm(`Delete sub-category "${s.name}"?`)) return;
    try { await api.delete(`/admin/subcategories/${s.id}`); await reloadSubs(subCat.id); toast.success("Deleted"); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-ink sm:text-3xl">Categories</h1>
          <p className="mt-1 text-ink-soft">Add a clear photo to each category so customers recognise it instantly.</p>
        </div>
        <Button onClick={openNew} className="rounded-full bg-brand text-ink hover:bg-brand-hover" data-testid="add-category-btn">
          <Plus className="mr-1 h-4 w-4" /> Add Category
        </Button>
      </div>

      {loading ? (
        <div className="mt-10 flex justify-center"><Loader2 className="h-8 w-8 animate-spin text-brand" /></div>
      ) : (
        <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {cats.map((c) => (
            <div key={c.id} className="rounded-2xl border border-border/60 bg-white p-5 shadow-ambient" data-testid={`admin-category-${c.id}`}>
              <div className="flex items-start justify-between">
                <div className="h-14 w-14 overflow-hidden rounded-2xl bg-cream-alt">
                  {c.image_id ? (
                    <img src={fileUrl(c.image_id)} alt={c.name} className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-ink-muted"><Tags className="h-5 w-5" /></div>
                  )}
                </div>
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${c.is_active ? "bg-sage/15 text-sage" : "bg-ink/10 text-ink-muted"}`}>{c.is_active ? "Active" : "Hidden"}</span>
              </div>
              <h3 className="mt-3 font-heading text-lg text-ink">{c.name}</h3>
              <p className="text-sm text-ink-muted line-clamp-2">{c.description || "—"}</p>
              <p className="mt-1 text-xs font-medium text-ink-soft">{c.product_count || 0} product{(c.product_count || 0) === 1 ? "" : "s"}</p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => openEdit(c)} className="rounded-full"><Pencil className="mr-1 h-3.5 w-3.5" /> Edit</Button>
                <Button variant="outline" size="sm" onClick={() => openSubs(c)} className="rounded-full" data-testid={`manage-subs-${c.id}`}><Layers className="mr-1 h-3.5 w-3.5" /> Sub-categories</Button>
                <Button variant="ghost" size="sm" onClick={() => remove(c)} className="rounded-full text-destructive"><Trash2 className="mr-1 h-3.5 w-3.5" /> Delete</Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">{editing ? "Edit Category" : "Add Category"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Category photo</Label>
              <div className="mt-2 flex items-center gap-4">
                <div className="h-20 w-20 shrink-0 overflow-hidden rounded-2xl bg-cream-alt">
                  {form.image_id ? (
                    <img src={fileUrl(form.image_id)} alt="" className="h-full w-full object-cover" />
                  ) : (
                    <div className="flex h-full items-center justify-center text-ink-muted"><ImageIcon className="h-6 w-6" /></div>
                  )}
                </div>
                <div>
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={upload} data-testid="category-image-input" />
                  <div className="flex flex-wrap gap-2">
                    <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-full">
                      {uploading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}
                      {form.image_id ? "Change photo" : "Upload photo"}
                    </Button>
                    {form.image_id && (
                      <Button type="button" variant="ghost" onClick={() => setForm((f) => ({ ...f, image_id: null }))} className="rounded-full text-destructive" data-testid="category-remove-image-btn">
                        <Trash2 className="mr-2 h-4 w-4" /> Remove
                      </Button>
                    )}
                  </div>
                  <p className="mt-1 text-xs text-ink-muted">Shown on the storefront · JPG, PNG, WEBP · max 5MB</p>
                </div>
              </div>
            </div>
            <div>
              <Label>Name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-xl" placeholder="e.g. Milk, Oils, Vegetables" data-testid="category-name-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="mt-1.5 rounded-xl" rows={2} />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Sort order</Label>
                <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} className="mt-1.5 rounded-xl" />
              </div>
              <div className="flex items-end justify-between rounded-xl bg-cream-alt/50 px-4 py-2">
                <Label>Active</Label>
                <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="category-active-switch" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full">Cancel</Button>
            <Button onClick={save} disabled={saving} className="rounded-full bg-brand text-ink" data-testid="save-category-btn">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={subOpen} onOpenChange={setSubOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle className="font-heading">Sub-categories · {subCat?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-ink-soft">Add tighter groups (e.g. Veg / Non-Veg, Cool Drinks) so shoppers see the right “similar products”.</p>
            <div className="flex gap-2">
              <Input value={subName} onChange={(e) => setSubName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addSub()} placeholder="e.g. Veg, Non-Veg, Exotic" className="rounded-xl" data-testid="subcategory-name-input" />
              <Button onClick={addSub} disabled={subBusy} className="rounded-full bg-brand text-ink" data-testid="add-subcategory-btn">{subBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add"}</Button>
            </div>
            {subs.length === 0 ? (
              <p className="py-6 text-center text-sm text-ink-muted">No sub-categories yet.</p>
            ) : (
              <div className="divide-y divide-border/60 rounded-xl border border-border/60">
                {subs.map((s) => (
                  <div key={s.id} className="flex items-center justify-between px-3 py-2" data-testid={`admin-subcategory-${s.id}`}>
                    <div>
                      <p className="text-sm font-semibold text-ink">{s.name}</p>
                      <p className="text-xs text-ink-muted">{s.product_count || 0} product{(s.product_count || 0) === 1 ? "" : "s"}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <Switch checked={!!s.is_active} onCheckedChange={() => toggleSub(s)} data-testid={`subcategory-toggle-${s.id}`} />
                      <Button variant="ghost" size="sm" onClick={() => removeSub(s)} className="rounded-full text-destructive" data-testid={`subcategory-delete-${s.id}`}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
